import { type PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/shared/buttons';
import { Modal } from '@/components/shared/Modals/Modal';
import { Body } from '@/components/shared/typography/Body';
import { useFeatureFlags } from '@/hooks/featureFlag/useFeatureFlags';
import { useAuthentication } from '@/hooks/user/useAuthentication';
import { ONE_MINUTE, ONE_SECOND } from '@/resources/time';

type IdleSessionTimeoutProps = PropsWithChildren<{
  // Envoy logout navigates synchronously; cleanup wrappers may still complete asynchronously first.
  signOut: () => void | Promise<void>;
  isSigningOut: boolean;
}>;

/**
 * The idle session belongs to the internal GreyScout user id (`gsUid`), so a support ticket naming
 * one reads straight back to the key and channel in devtools. The claim comes from Envoy's OIDC
 * userinfo response and is unvalidated at runtime: anything that is not a positive whole number names nobody
 * and yields no names at all, rather than a `greyscout:idle-session:undefined:...` nobody owns.
 */
export const buildIdleSessionNames = (gsUid: unknown) => {
  const isCoercibleClaim = (typeof gsUid === 'number' || typeof gsUid === 'string') && gsUid !== '';
  const userId = isCoercibleClaim ? Number(gsUid) : Number.NaN;
  if (!Number.isInteger(userId) || userId <= 0) return undefined;

  return {
    activityStorageKey: `greyscout:idle-session:${userId}:last-activity`,
    activityChannel: `greyscout:idle-session:${userId}`,
  };
};

export const formatCountdown = (remainingSeconds: number) => {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const IdleSessionTimeout = ({
  children,
  signOut,
  isSigningOut,
}: IdleSessionTimeoutProps) => {
  const { t } = useTranslation();
  const { idleLogout } = useFeatureFlags();
  const inactivityTimeoutMs = idleLogout?.enabled && idleLogout?.timeoutMinutes
    ? idleLogout.timeoutMinutes * ONE_MINUTE
    : 0;

  const warningLeadMs = Math.min(2 * ONE_MINUTE, inactivityTimeoutMs / 2);
  const warningTimeoutMs = inactivityTimeoutMs - warningLeadMs;

  const isIdleLogoutConfigured = Number.isFinite(inactivityTimeoutMs) && inactivityTimeoutMs > 0;

  const { user } = useAuthentication();
  // No owner, no key and no channel — and so no timer against a session nobody owns.
  const { activityStorageKey, activityChannel } = buildIdleSessionNames(user?.gsUid) ?? {};
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const warningTimer = useRef<ReturnType<typeof setTimeout>>();
  const logoutTimer = useRef<ReturnType<typeof setTimeout>>();
  const logoutAt = useRef(0);
  const channel = useRef<BroadcastChannel | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const activeStorageKey = useRef<string | null>(null);
  const isSigningOutRef = useRef(false);
  const isWarningOpenRef = useRef(false);

  useEffect(() => {
    isWarningOpenRef.current = isWarningOpen;
  }, [isWarningOpen]);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimer.current);
    clearTimeout(logoutTimer.current);
  }, []);

  const logout = useCallback(() => {
    if (isSigningOutRef.current) return;
    isSigningOutRef.current = true;
    clearTimers();
    setIsWarningOpen(false);
    void signOut();
  }, [clearTimers, signOut]);

  const schedule = useCallback((lastActivity: number) => {
    clearTimers();
    if (!isIdleLogoutConfigured || !activityStorageKey) return;
    const elapsed = Date.now() - lastActivity;
    logoutAt.current = lastActivity + inactivityTimeoutMs;
    if (elapsed >= inactivityTimeoutMs) {
      logout();
      return;
    }
    if (elapsed >= warningTimeoutMs) {
      setIsWarningOpen(true);
    } else {
      setIsWarningOpen(false);
      warningTimer.current = setTimeout(() => setIsWarningOpen(true), warningTimeoutMs - elapsed);
    }
    logoutTimer.current = setTimeout(logout, inactivityTimeoutMs - elapsed);
  }, [activityStorageKey, clearTimers, inactivityTimeoutMs, isIdleLogoutConfigured, logout, warningTimeoutMs]);

  const recordActivity = useCallback((lastActivity = Date.now(), force = false) => {
    if ((!force && isWarningOpenRef.current) || isSigningOutRef.current) return;
    if (!activityStorageKey) return;
    window.localStorage.setItem(activityStorageKey, String(lastActivity));
    channel.current?.postMessage(lastActivity);
    schedule(lastActivity);
  }, [activityStorageKey, schedule]);

  const staySignedIn = useCallback(() => {
    setIsWarningOpen(false);
    recordActivity(Date.now(), true);
  }, [recordActivity]);

  useEffect(() => {
    if (!isWarningOpen) return undefined;

    const updateRemainingTime = () => {
      setRemainingSeconds(Math.max(0, Math.ceil((logoutAt.current - Date.now()) / ONE_SECOND)));
    };

    updateRemainingTime();
    const timer = setInterval(updateRemainingTime, ONE_SECOND);

    return () => clearInterval(timer);
  }, [isWarningOpen]);

  useEffect(() => {
    if (!isIdleLogoutConfigured || !activityStorageKey || !activityChannel) {
      clearTimers();
      setIsWarningOpen(false);
      activeStorageKey.current = null;
      return undefined;
    }

    channel.current = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(activityChannel);
    channel.current?.addEventListener('message', (event: MessageEvent<number>) => {
      setIsWarningOpen(false);
      schedule(event.data);
    });

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== activityStorageKey || !event.newValue) return;
      setIsWarningOpen(false);
      schedule(Number(event.newValue));
    };
    const handleActivity = () => recordActivity();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // An absent entry reads as 0, which would date the session to the epoch and sign out at once.
        const lastActivity = Number(window.localStorage.getItem(activityStorageKey));
        schedule(Number.isFinite(lastActivity) && lastActivity > 0 ? lastActivity : Date.now());
      }
    };

    window.addEventListener('pointerdown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity, true);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const lastActivity = Number(window.localStorage.getItem(activityStorageKey));
    // Preserve elapsed time on flag updates, but start fresh for a new session.
    if (activeStorageKey.current === activityStorageKey && Number.isFinite(lastActivity) && lastActivity > 0) {
      schedule(lastActivity);
    } else {
      recordActivity(Date.now(), true);
    }
    activeStorageKey.current = activityStorageKey;
    return () => {
      clearTimers();
      channel.current?.close();
      channel.current = null;
      window.removeEventListener('pointerdown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity, true);
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activityChannel, activityStorageKey, clearTimers, isIdleLogoutConfigured, recordActivity, schedule]);

  return (
    <>
      {children}
      <Modal isOpen={isWarningOpen} handleCancel={() => { }}>
        <Modal.Header title={t('common:idleSessionTimeout.title')} />
        <Body size='large'>
          {t('common:idleSessionTimeout.description', { time: formatCountdown(remainingSeconds) })}
        </Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={logout}
            disabled={isSigningOut}>
            {t('common:idleSessionTimeout.signOutNow')}
          </Button>
          <Button onClick={staySignedIn} disabled={isSigningOut}>
            {t('common:idleSessionTimeout.staySignedIn')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
